import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useUpdateTodo } from '@/lib/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useForm } from 'react-hook-form';
import {
  requestEditorActionsClasses,
  requestEditorHintClasses,
  requestEditorTextareaClasses,
  sheetBodyClasses,
} from '../bloodTests.styles';
import {
  type BloodTestOrderFormProps,
  type BloodTestRequestFormValues,
  bloodTestRequestSchema,
} from '../bloodTests.types';

/** Outside the ordering gate a blood-test task is a text request: edit it, save it, mark it done. */
export const BloodTestRequestEditor = ({ task, onClose, onOrdered }: BloodTestOrderFormProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const form = useForm<BloodTestRequestFormValues>({
    resolver: zodResolver(bloodTestRequestSchema),
    defaultValues: { details: task.details ?? '' },
  });
  const save = useUpdateTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: () => toast({ title: 'Ekki tókst að vista blóðprufubeiðnina', variant: 'destructive' }),
  });

  const toTodoUpdate = ({ details }: BloodTestRequestFormValues) => ({
    id: task.todoId,
    data: { description: details },
  });

  const handleSave = form.handleSubmit((values) => save.mutate(toTodoUpdate(values), { onSuccess: onClose }));
  const handleConfirm = form.handleSubmit((values) =>
    save.mutate(toTodoUpdate(values), {
      onSuccess: () => {
        onOrdered();
        onClose();
      },
    })
  );

  return (
    <Form {...form}>
      <form className={sheetBodyClasses} onSubmit={handleSave}>
        <FormField
          control={form.control}
          name="details"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Beiðni</FormLabel>
              <FormControl>
                <Textarea {...field} className={requestEditorTextareaClasses} />
              </FormControl>
            </FormItem>
          )}
        />
        <p className={requestEditorHintClasses}>Pöntun blóðprufa beint til Sameindar er ekki virk fyrir stöðina.</p>
        <div className={requestEditorActionsClasses}>
          <Button type="submit" variant="outline" disabled={save.isPending}>
            Vista
          </Button>
          <Button type="button" disabled={save.isPending} onClick={handleConfirm}>
            <Check className="h-4 w-4" />
            Staðfesta
          </Button>
        </div>
      </form>
    </Form>
  );
};
